import React, { useCallback, useEffect, useState } from 'react';

import { Modal } from 'src/components/Modal/Modal';
import { Button } from 'src/components/Button';
import { Container } from 'src/components/Container';
import { Table } from 'src/components/TableNew';
import NoResult from 'src/components/NoResult/NoResult';
import { PermissionGuard } from 'src/components/PermissionGuard';
import { FilterBar, type FilterDef } from 'src/components/FilterBar';
import { useFilterQueryEffect } from 'src/hooks/useFilterQueryEffect';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { PERMISSIONS } from 'src/utils/permissions';
import { convertDate } from 'src/components/Table/helpers';
import {
  NotificationCampaign,
  NotificationDelivery,
  campaignErrorMessage,
  cancelCampaign,
  describeAudience,
  fetchCampaign,
  fetchCampaignDeliveries,
  retryCampaign,
} from 'src/api/adminNotifications';

import { ActionRow, Callout, CellStack, ErrorText, MetaLine, Muted, StatCard, StatLabel, StatValue, StatsRow, StatusBadge } from './styled';

const DELIVERY_FILTERS: FilterDef[] = [
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'QUEUED', label: 'Queued' },
      { value: 'DELIVERED', label: 'Delivered' },
      { value: 'FAILED', label: 'Failed' },
      { value: 'SKIPPED', label: 'Skipped' },
    ],
    allLabel: 'All',
  },
  {
    key: 'email_status',
    label: 'Email',
    options: [
      { value: 'SENT', label: 'Sent' },
      { value: 'FAILED', label: 'Failed' },
      { value: 'NO_ADDRESS', label: 'No address' },
      { value: 'NOT_REQUESTED', label: 'Not requested' },
    ],
    allLabel: 'All',
  },
  { key: 'email', label: 'User email', placeholder: 'Starts with…' },
  { key: 'name', label: 'User name', placeholder: 'First or "first last"' },
];

const HEADERS = ['User', 'Status', 'Error', 'Delivered at'];

const LIVE_STATUSES = ['PREPARING', 'QUEUED', 'PROCESSING'];
const POLL_MS = 5000;

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const { date, time } = convertDate(value);
  return `${date} ${time}`;
};

type CampaignDetailModalProps = {
  campaignId: string;
  onClose: () => void;
  onChanged?: () => void;
};

/**
 * Counts up top (polled while the campaign is running), who-got-what below.
 */
const CampaignDetailModal: React.FC<CampaignDetailModalProps> = ({ campaignId, onClose, onChanged }) => {
  const dispatch = useDispatch();
  const [campaign, setCampaign] = useState<NotificationCampaign | null>(null);
  const [deliveries, setDeliveries] = useState<NotificationDelivery[]>([]);
  const [loadingRows, setLoadingRows] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [acting, setActing] = useState(false);

  const loadCampaign = useCallback(async () => {
    try {
      setCampaign(await fetchCampaign(campaignId));
    } catch (e: any) {
      dispatch(setAppAlert({ message: campaignErrorMessage(e, 'Failed to load campaign'), isSuccess: false }));
      onClose();
    }
  }, [campaignId, dispatch, onClose]);

  const loadRows = useCallback(
    async (params: { page: number; filters: Record<string, string> }) => {
      setLoadingRows(true);
      try {
        const res = await fetchCampaignDeliveries(campaignId, { page: params.page, limit: 20, ...params.filters });
        setDeliveries(res.deliveries);
        setTotalPages(Math.max(1, res.meta.page_count || 1));
      } catch (e: any) {
        dispatch(setAppAlert({ message: campaignErrorMessage(e, 'Failed to load deliveries'), isSuccess: false }));
        setDeliveries([]);
      } finally {
        setLoadingRows(false);
      }
    },
    [campaignId, dispatch],
  );

  const { applied: appliedFilters, refresh: refreshQuery } = useFilterQueryEffect({ filters, currentPage, setCurrentPage, query: loadRows });

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  // While the worker is draining, refresh counts and the visible rows.
  const live = Boolean(campaign && LIVE_STATUSES.includes(String(campaign.status)));
  useEffect(() => {
    if (!live) return undefined;
    const timer = window.setInterval(() => {
      loadCampaign();
      loadRows({ page: currentPage, filters: appliedFilters });
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [live, loadCampaign, loadRows, currentPage, appliedFilters]);

  const act = async (label: string, run: () => Promise<unknown>) => {
    setActing(true);
    try {
      await run();
      dispatch(setAppAlert({ message: label, isSuccess: true }));
      await loadCampaign();
      await loadRows({ page: currentPage, filters: appliedFilters });
      onChanged?.();
    } catch (e: any) {
      dispatch(setAppAlert({ message: campaignErrorMessage(e, 'Action failed'), isSuccess: false }));
    } finally {
      setActing(false);
    }
  };

  const rows = deliveries.map((d) => ({
    ...d,
    user_name: [d.name?.first, d.name?.last].filter(Boolean).join(' '),
    email_display: d.email || '—',
    delivered_display: formatDateTime(d.delivered_at),
  }));

  const counts = campaign?.counts;
  const status = String(campaign?.status || '');

  return (
    <Modal isOpen onClose={onClose} wide>
      <Container display="flex" justifyContent="space-between" alignItems="flex-start" gap="1rem" paddingBottom="0.5rem">
        <div>
          <Container fontWeight={600} fontSize="1.25rem">
            {campaign?.title ?? 'Campaign'}
          </Container>
          <MetaLine>
            {campaign ? (
              <>
                {describeAudience(campaign.audience)} · {campaign.channels.email ? 'In-app + email' : 'In-app'} · by{' '}
                {campaign.created_by_email || '—'} · {formatDateTime(campaign.created_at)}
              </>
            ) : (
              'Loading…'
            )}
          </MetaLine>
        </div>
        {campaign ? <StatusBadge $status={status}>{status}</StatusBadge> : null}
      </Container>

      {campaign ? (
        <Callout>{campaign.message}</Callout>
      ) : null}
      {campaign?.last_error ? (
        <Container paddingTop="0.5rem">
          <ErrorText>Worker error: {campaign.last_error}</ErrorText>
        </Container>
      ) : null}

      <div style={{ height: '1rem' }} />
      <StatsRow>
        <StatCard>
          <StatLabel>Targeted</StatLabel>
          <StatValue>{counts?.targeted ?? '—'}</StatValue>
        </StatCard>
        <StatCard $tone="muted">
          <StatLabel>Queued</StatLabel>
          <StatValue>{counts?.queued ?? '—'}</StatValue>
        </StatCard>
        <StatCard $tone="positive">
          <StatLabel>Delivered</StatLabel>
          <StatValue>{counts?.delivered ?? '—'}</StatValue>
        </StatCard>
        <StatCard $tone="negative">
          <StatLabel>Failed</StatLabel>
          <StatValue>{counts?.failed ?? '—'}</StatValue>
        </StatCard>
        <StatCard $tone="muted">
          <StatLabel>Skipped</StatLabel>
          <StatValue>{counts?.skipped ?? '—'}</StatValue>
        </StatCard>
        <StatCard $tone={counts?.email_failed ? 'negative' : 'positive'}>
          <StatLabel>Email sent / failed</StatLabel>
          <StatValue>
            {campaign?.channels.email ? `${counts?.email_sent ?? 0} / ${counts?.email_failed ?? 0}` : <Muted>off</Muted>}
          </StatValue>
        </StatCard>
      </StatsRow>

      <PermissionGuard permission={PERMISSIONS.USER_WRITE}>
        <ActionRow>
          {LIVE_STATUSES.includes(status) ? (
            <Button
              text={acting ? 'Working…' : 'Cancel remaining'}
              className="outline"
              disabled={acting}
              onClick={() => act('Campaign canceled — queued deliveries skipped', () => cancelCampaign(campaignId))}
            />
          ) : null}
          {['COMPLETED', 'CANCELED'].includes(status) && ((counts?.failed ?? 0) > 0 || (counts?.skipped ?? 0) > 0) ? (
            <Button
              text={acting ? 'Working…' : 'Retry failed & skipped'}
              className="black"
              disabled={acting}
              onClick={() => act('Re-queued — delivery resumes within a minute', () => retryCampaign(campaignId))}
            />
          ) : null}
        </ActionRow>
      </PermissionGuard>

      <FilterBar
        filters={DELIVERY_FILTERS}
        values={filters}
        onChange={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))}
        onClear={() => setFilters({})}
        onRefresh={refreshQuery}
      />

      {rows.length > 0 || loadingRows ? (
        <Table
          loading={loadingRows}
          headers={HEADERS}
          items={rows}
          columns="1.6fr 1fr 1.4fr 0.9fr"
          fieldsToShow={['user_name', 'status', 'error', 'delivered_display']}
          sortableFields={[]}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          disableRowDetails
          renderCell={(item, field) => {
            // Name over email, one cell: the name is often empty on staging accounts.
            if (field === 'user_name') {
              return (
                <CellStack>
                  <strong>{item.user_name || item.email_display}</strong>
                  {item.user_name ? <Muted>{item.email_display}</Muted> : null}
                </CellStack>
              );
            }
            // In-app result as the badge; the email result underneath only when email was on.
            if (field === 'status') {
              return (
                <CellStack>
                  <StatusBadge $status={String(item.status)}>{String(item.status)}</StatusBadge>
                  {campaign?.channels.email ? (
                    <Muted>Email: {String(item.email_status).replace(/_/g, ' ').toLowerCase()}</Muted>
                  ) : null}
                </CellStack>
              );
            }
            if (field === 'error') return item.error ? <ErrorText>{item.error}</ErrorText> : <Muted>—</Muted>;
            return String(item[field] ?? '—');
          }}
        />
      ) : (
        <NoResult />
      )}
    </Modal>
  );
};

export default CampaignDetailModal;
